import { useState } from 'react';
import { CopyIcon, CheckIcon } from './icons';

/**
 * Click-to-copy `<code>` block with a double-square icon that fades
 * in on hover.  Click anywhere on the block to copy `value` to the
 * clipboard; the icon briefly flips to a checkmark to confirm.
 *
 * Use everywhere we surface a value the operator is meant to paste
 * elsewhere — DNS records, API key prefixes, signing secrets, etc.
 * Replaces the older pattern of a separate `<code>` + `[Copy]`
 * button next to it.
 *
 * Props:
 *   - value: the string to copy.  Renders as `—` when null/empty.
 *   - label: optional ARIA / title text, e.g. `"Copy DKIM record"`.
 *   - inline: render inline (default) vs full-width block.
 */
export function CopyableCode({ value, label, inline = false }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    if (value == null || value === '') return;
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard rejected (insecure context, perms, etc.) — the
      // value is still visible inline so the user can highlight + ⌘C.
    }
  }

  const titleText = copied
    ? 'Copied'
    : `Copy${label ? ` ${label}` : ''} to clipboard`;

  return (
    <button
      type="button"
      onClick={copy}
      title={titleText}
      aria-label={titleText}
      className={`copyable-code${inline ? ' copyable-code--inline' : ''}${copied ? ' copyable-code--copied' : ''}`}
    >
      <code className="copyable-code__value">
        {value == null || value === '' ? '—' : String(value)}
      </code>
      <span className="copyable-code__icon" aria-hidden="true">
        {copied ? <CheckIcon /> : <CopyIcon />}
      </span>
    </button>
  );
}

export const COPYABLE_CODE_CSS = `
  .copyable-code {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 6px 8px;
    background: var(--surface-2);
    border: 1px solid transparent;
    border-radius: 4px;
    cursor: pointer;
    font: inherit;
    text-align: left;
    color: inherit;
    transition: border-color 0.15s, background 0.15s;
  }
  .copyable-code--inline {
    width: auto;
    display: inline-flex;
  }
  .copyable-code:hover {
    border-color: var(--surface-3, #d0d4dc);
  }
  .copyable-code:focus-visible {
    outline: 2px solid var(--brand-500, #5b6cff);
    outline-offset: 1px;
  }
  .copyable-code--copied {
    background: var(--success-100, #e6f7ec);
    border-color: var(--success-400, #58c47a);
  }
  .copyable-code__value {
    flex: 1;
    background: transparent;
    padding: 0;
    font-size: 12px;
    word-break: break-all;
    color: var(--text-1);
  }
  .copyable-code__icon {
    flex-shrink: 0;
    opacity: 0.35;
    color: var(--text-2);
    display: inline-flex;
    transition: opacity 0.15s, color 0.15s;
  }
  .copyable-code:hover .copyable-code__icon,
  .copyable-code:focus-visible .copyable-code__icon {
    opacity: 1;
  }
  .copyable-code--copied .copyable-code__icon {
    opacity: 1;
    color: var(--success-700, #1a7a3a);
  }
`;
